import React, {useEffect, useRef, useState} from 'react';
import {App, Button, Checkbox, InputNumber, Modal, Segmented, Spin} from 'antd';
import {useI18n} from '../../../i18n/useI18n';
import {getDcaUserConfig, parseDcaSymbolList, updateDcaUserConfig} from '../../../services/dcaService';
import type {DcaUserConfig} from '../../../types/dca';

interface Props {
    open: boolean;
    onClose: () => void;
    onSaved: (config: DcaUserConfig) => void;
}

/**
 * 用户级 DCA 设置弹窗
 * 单次金额/通知策略/单日买入总额上限/可见与可选品种（三层嵌套）；
 * 保存前本地收窄 allowed ⊆ visible，后端强校验兜底。
 */
const DcaSettingsModal: React.FC<Props> = ({open, onClose, onSaved}) => {
    const {t} = useI18n();
    const {message} = App.useApp();

    const [config, setConfig] = useState<DcaUserConfig | null>(null);
    const [saving, setSaving] = useState(false);
    /**
     * 配置是否已成功加载：未加载完成时禁止保存。
     * 否则「加载失败 → 品种列表保持初始空数组 → 保存空串」会清空白名单，
     * 后端随即自动暂停该用户全部运行中计划（P0-3）。
     */
    const [configLoaded, setConfigLoaded] = useState(false);
    const [amount, setAmount] = useState<number>(50);
    const [notifyStrategy, setNotifyStrategy] = useState<'EVERY' | 'DAILY_SUMMARY' | 'OFF'>('EVERY');
    const [dailyLimit, setDailyLimit] = useState<number | null>(null);
    const [visibleSymbols, setVisibleSymbols] = useState<string[]>([]);
    const [allowedSymbols, setAllowedSymbols] = useState<string[]>([]);

    /**
     * onClose 经 ref 读取：父组件可能传入内联箭头函数（每次渲染新身份），
     * 若直接进依赖数组会与外部轮询共同导致配置被反复拉取。
     */
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    });

    useEffect(() => {
        if (!open) {
            return;
        }
        setConfigLoaded(false);
        getDcaUserConfig()
            .then((cfg) => {
                if (null == cfg) {
                    // 接口成功但无配置数据：等同加载失败，不能停留在「可保存的空表单」
                    throw new Error(t('dca.page.configLoadError'));
                }
                setConfig(cfg);
                setAmount(Number(cfg.amountUsdt ?? 50));
                setNotifyStrategy(cfg.notifyStrategy ?? 'EVERY');
                setDailyLimit(null == cfg.dailyBuyLimitUsdt ? null : Number(cfg.dailyBuyLimitUsdt));
                setVisibleSymbols(parseDcaSymbolList(cfg.visibleSymbols));
                setAllowedSymbols(parseDcaSymbolList(cfg.allowedSymbols));
                setConfigLoaded(true);
            })
            .catch((e: any) => {
                message.error(e?.message ?? t('dca.page.configLoadError'));
                setConfig(null);
                setConfigLoaded(false);
                // 加载失败：直接关闭弹窗，不渲染「已加载外观」的空表单
                onCloseRef.current();
            });
    }, [open, message, t]);

    const globalSymbols = config?.globalAvailableSymbols ?? [];

    const handleVisibleChange = (checked: string[]) => {
        setVisibleSymbols(checked);
        // 可见收窄时同步收窄可选，保证 allowed ⊆ visible
        setAllowedSymbols((prev) => prev.filter((s) => checked.includes(s)));
    };

    const handleSave = async () => {
        if (!configLoaded || null == config) {
            message.error(t('dca.settings.configNotLoaded'));
            return;
        }
        if (null == amount || amount <= 0) {
            message.warning(t('dca.settings.amountRequired'));
            return;
        }
        if (null != dailyLimit && dailyLimit < 0) {
            message.warning(t('dca.form.paramInvalid'));
            return;
        }
        setSaving(true);
        try {
            const saved = await updateDcaUserConfig({
                visibleSymbols: visibleSymbols.join(','),
                allowedSymbols: allowedSymbols.join(','),
                // 逐字段显式声明「清空」：否则空串会被统一视为「不修改」，
                // 导致「保留可见品种、清空可选品种」被静默忽略（P1 修复）
                clearVisible: 0 === visibleSymbols.length,
                clearAllowed: 0 === allowedSymbols.length,
                amountUsdt: amount,
                notifyStrategy,
                // 清空单日上限=0（后端 0→null=不限）
                dailyBuyLimitUsdt: null == dailyLimit ? 0 : dailyLimit,
            });
            message.success(t('dca.settings.saveOk'));
            if (null != saved) {
                onSaved(saved);
            }
            onClose();
        } catch (e: any) {
            message.error(e?.message ?? t('dca.settings.saveFail'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            title={t('dca.settings.title')}
            width={560}
            onCancel={onClose}
            maskClosable={false}
            confirmLoading={saving}
            footer={[
                <Button key="cancel" onClick={onClose}>{t('dca.common.cancel')}</Button>,
                <Button key="save" type="primary" loading={saving} disabled={!configLoaded}
                        onClick={() => void handleSave()}>
                    {t('dca.settings.save')}
                </Button>,
            ]}
        >
            <div className="dca-form">
                {!configLoaded ? (
                    <div className="dca-form-tip dca-settings-loading">
                        <Spin size="small"/> {t('dca.settings.loading')}
                    </div>
                ) : (
                    <>
                        <div className="dca-form-row">
                            <div className="dca-form-item">
                                <div className="dca-form-label">{t('dca.settings.amount')}</div>
                                <InputNumber min={0.01} step={1} value={amount} style={{width: 180}}
                                             max={config?.maxUserAmountUsdt ?? undefined}
                                             onChange={(v) => setAmount(v ?? 50)}/>
                            </div>
                            <div className="dca-form-item">
                                <div className="dca-form-label">{t('dca.settings.dailyLimit')}</div>
                                <InputNumber min={0} value={dailyLimit} style={{width: 180}}
                                             max={config?.maxUserDailyBuyLimitUsdt ?? undefined}
                                             placeholder={t('dca.settings.dailyLimitPlaceholder')}
                                             onChange={(v) => setDailyLimit(v ?? null)}/>
                            </div>
                        </div>
                        <div className="dca-form-tip dca-settings-limit-tip">
                            {t('dca.settings.platformLimitTip')
                                .replace('{amount}', String(config?.maxUserAmountUsdt ?? 100))
                                .replace('{daily}', String(config?.maxUserDailyBuyLimitUsdt ?? 500))
                                .replace('{count}', String(config?.maxPlanBuyCount ?? 50))
                                .replace('{triggers}', String(config?.maxPlanDailyTriggers ?? 20))}
                        </div>
                        <div className="dca-form-item">
                            <div className="dca-form-label">{t('dca.settings.notify')}</div>
                            <Segmented
                                value={notifyStrategy}
                                options={[
                                    {label: t('dca.settings.notify.EVERY'), value: 'EVERY'},
                                    {label: t('dca.settings.notify.DAILY_SUMMARY'), value: 'DAILY_SUMMARY'},
                                    {label: t('dca.settings.notify.OFF'), value: 'OFF'},
                                ]}
                                onChange={(v) => setNotifyStrategy(v as 'EVERY' | 'DAILY_SUMMARY' | 'OFF')}
                            />
                        </div>
                        <div className="dca-form-item">
                            <div className="dca-form-label">{t('dca.settings.visible')}</div>
                            <div className="dca-form-tip">{t('dca.settings.visibleTip')}</div>
                            {0 === globalSymbols.length ? (
                                <div className="dca-form-tip">{t('dca.settings.noGlobal')}</div>
                            ) : (
                                <Checkbox.Group options={globalSymbols} value={visibleSymbols}
                                                onChange={(v) => handleVisibleChange(v as string[])}/>
                            )}
                        </div>
                        <div className="dca-form-item">
                            <div className="dca-form-label">{t('dca.settings.allowed')}</div>
                            <div className="dca-form-tip">{t('dca.settings.allowedTip')}</div>
                            {0 === visibleSymbols.length ? (
                                <div className="dca-form-tip">{t('dca.form.symbolEmpty')}</div>
                            ) : (
                                <Checkbox.Group options={visibleSymbols} value={allowedSymbols}
                                                onChange={(v) => setAllowedSymbols(v as string[])}/>
                            )}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default DcaSettingsModal;
